import { Box } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { getIn, useFormikContext } from "formik";
import React from "react";
import { useGoogleMapsApi } from "../../../google-maps/useGoogleMapsApi.js";
import {
  getAddressInfoFromPlace,
  getLocationFromPlace,
  GoogleAddressSearch,
} from "../../../commons/GoogleAddressSearch.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import type { OnboardingFormValues } from "./mergePrefillIntoFormValues.js";

/**
 * The street field of the address. While the Google Places service is loaded it
 * suggests addresses as the user types; picking one fills street, postcode,
 * city and country and confirms the address with its location. Without Places
 * (script blocked, no key) it is a plain text field, and the server geocodes
 * the saved address.
 */
export const OnboardingStreetField = () => {
  const { t } = useTranslate(["OrganisationProfile"]);
  const { isLoaded } = useGoogleMapsApi();
  const { values, errors, touched, setFieldValue } =
    useFormikContext<OnboardingFormValues>();
  const label = t("Street and number", { ns: "OrganisationProfile" });
  const setInputValueRef = React.useRef<((text: string) => void) | undefined>(
    undefined
  );

  // The search box keeps its own text once the user has typed; whatever else
  // changes the street (a website lookup, a picked suggestion, Undo) is pushed in.
  const street = values.address.streetLines1;
  React.useEffect(() => {
    setInputValueRef.current?.(street);
  }, [street]);

  if (!isLoaded) {
    return (
      <TextField
        name="address.streetLines1"
        label={label}
        fullWidth
        onChange={event =>
          setFieldValue("address", {
            ...values.address,
            streetLines1: event.target.value,
            // Typed, so any coordinates no longer describe it.
            location: null,
          })
        }
      />
    );
  }

  const error = getIn(touched, "address.streetLines1")
    ? getIn(errors, "address.streetLines1")
    : undefined;

  return (
    <Box>
      <GoogleAddressSearch<string>
        variant="filled"
        label={label}
        initialValue={street}
        setInputValueRef={setInputValueRef}
        // The suggestion reads "Kurfürstendamm 15, Berlin, Germany"; the box
        // shows the street line only, the rest goes to the other fields.
        keepTextOnPick
        error={typeof error === "string" ? error : undefined}
        onInputChange={text =>
          setFieldValue("address", {
            ...values.address,
            streetLines1: text,
            location: null,
          })
        }
        onChange={picked => {
          if (!picked) {
            // The search box was cleared.
            void setFieldValue("address", {
              ...values.address,
              streetLines1: "",
              location: null,
            });
            return;
          }
          if ("place" in picked) {
            const info = getAddressInfoFromPlace(picked.place);
            setInputValueRef.current?.(info.streetLines1);
            void setFieldValue("address", {
              streetLines1: info.streetLines1,
              postalCode: info.postalCode,
              city: info.city,
              countryCode: info.countryCode || values.address.countryCode,
              location: getLocationFromPlace(picked.place),
            });
          }
        }}
      />
    </Box>
  );
};
